import React from 'react';
import { Link } from 'react-router-dom';
import { useCourses } from '../context/CourseContext';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { CourseCard } from '../components/CourseCard';
import { motion, useAnimate, useInView } from 'motion/react';
import { ArrowRight, Play, Users, Award, Zap, Video, BarChart3 } from 'lucide-react';
import { optimizeImageUrl } from '../utils/image';

const Counter: React.FC<{ value: number; suffix?: string; duration?: number }> = ({ value, suffix = '', duration = 2 }) => {
  const [scope, animate] = useAnimate();
  const isInView = useInView(scope, { once: false, amount: 0.1 });

  React.useEffect(() => {
    if (isInView) {
      const controls = animate(0, value, {
        duration,
        onUpdate: (latest) => {
          if (scope.current) {
            scope.current.textContent = Math.floor(latest).toLocaleString() + suffix;
          }
        },
      });
      return () => controls.stop();
    } else {
      if (scope.current) {
        scope.current.textContent = '0' + suffix;
      }
    }
  }, [isInView, value, animate, suffix, duration, scope]);

  return <span ref={scope}>0{suffix}</span>;
};

export const Home: React.FC = () => {
  const { courses, homepageSettings, loading } = useCourses();
  const visibleCourses = React.useMemo(() => courses.filter(c => !c.isHidden), [courses]);
  const { isAdmin } = useAuth();
  const [actualStats, setActualStats] = React.useState({
    students: 0,
    courses: 0,
    successRate: 98
  });

  React.useEffect(() => {
    const fetchActualStats = async () => {
      // Only fetch lightweight course list size, avoiding heavy collection scans of all 'users' and 'enrollments'
      // to prevent "Quota Exceeded" / "Rate Exceeded" errors on page loads.
      if (!isAdmin) return;

      try {
        setActualStats({
          students: homepageSettings?.statsStudents || 10500,
          courses: visibleCourses.length > 0 ? visibleCourses.length : (homepageSettings?.statsCourses || 50),
          successRate: homepageSettings?.statsSuccessRate || 98
        });
      } catch (error) {
        console.error("Error setting actual stats:", error);
      }
    };
    fetchActualStats();
  }, [visibleCourses.length, isAdmin, homepageSettings]);

  const settings = {
    heroTitle: homepageSettings?.heroTitle || 'Learn Smart, Grow Fast with EduCoreBD',
    heroSubtitle: homepageSettings?.heroSubtitle || 'বাংলাদেশের শিক্ষার্থীদের জন্য আধুনিক সিলেবাস অনুযায়ী সেরা অনলাইন কোর্স এবং লাইভ ক্লাস। আজই শুরু করুন আপনার সফলতার যাত্রা।',
    schoolImage: homepageSettings?.schoolImage || 'https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEiCb9ut6h-z9ccNWqc7GIvAasyLR618NC-Dh857MfJk_tCqvozjo0hGyxXHk22zZgmuxWbF1e_Jw7h0uZiynrpJ0re6hrrovZL9DKy-94AcveL1_o2x6aR2AqnOxPd02Dx9b7lChVNpsUlQM1CNaVX_ZcVTA5ymOQj3lFWDhZfjZWSAItGy_iekaTqN13k/s1600/class%206-8.webp',
    academicImage: homepageSettings?.academicImage || 'https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEg1DXMsjCUDcM5gv90xQeEWajxCaElP827PS2wySOSy0TOwteGUcbHyOnFbqBhSBP6dFEm8AU6DLKW3Tf9yvlGwyNVyecit2yEzzMjDw1sM_twCIjz-D_iUl5eOF4qscs4MFyNRhPe-61T8OXSZ-h76tSnQkoniLcEkDfw25g0OhWmlAuQfkqbvjKm-H7o/s1600/academic.webp',
    crcImage: homepageSettings?.crcImage || 'https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEjJ12rJpneoLTjrCFm8OzhtokKsvZql4TRLayw6XmmnoFFy2teGYgEFWlBoccDG-8GPynNBL7PHtErefq9ioUZIE31uYggqX2-LY2ZhhdVhjLTXFlc9AdlaEr1h88YFwe0XQaMtDCSOh9jXu9z0M55lPrUCCbXht3_LTczJxflgIb8pyrYQVnITDQT7-Fs/s1600/crc.webp',
    skillImage: homepageSettings?.skillImage || 'https://picsum.photos/seed/skills/800/600',
    freeClassImage: homepageSettings?.freeClassImage || 'https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEieg0lwJKmohCYKk6pjPSeMGJ2Sj47wxTaMF5N2Voy8Pn_8R_rdVJGqvfrMvtsCKqsuVvkLlDBu-Mmk4pdr8k2ww1DXrNxxeAKfj2DOKDLsTJfwfJ4eO6Razz2sUQ5u_hTs9GOmmeQrgM-Ydsc4j6g6fzQfxi_pMfSIcBOh5v17Id_kSQY_YUv0MZ8KCj4/s0/kM73WBwARIGpmn9-hSCX6g@2k.webp',
    freeClassTitle: homepageSettings?.freeClassTitle || 'আমাদের ফ্রি মাস্টার ক্লাসগুলো দেখুন',
    freeClassSubtitle: homepageSettings?.freeClassSubtitle || 'কোর্স কেনার আগে আমাদের ফ্রি মাস্টার ক্লাসগুলো দেখে নিন। আমরা সেরা মানের শিক্ষা নিশ্চিত করি।',
    statsStudents: (homepageSettings?.isAutoStats || homepageSettings?.isAutoStatsV3) 
      ? actualStats.students 
      : (homepageSettings?.statsStudents || 10000),
    statsCourses: (homepageSettings?.isAutoStats || homepageSettings?.isAutoStatsV2 || homepageSettings?.isAutoStatsV3) 
      ? actualStats.courses 
      : (homepageSettings?.statsCourses || 50),
    statsTeachers: homepageSettings?.statsTeachers || 20,
    statsSuccessRate: homepageSettings?.isAutoStats 
      ? actualStats.successRate 
      : (homepageSettings?.statsSuccessRate || 98),
    heroButton1Text: homepageSettings?.heroButton1Text || 'কোর্সসমূহ দেখুন',
    heroButton2Text: homepageSettings?.heroButton2Text || 'ফ্রি মাস্টার ক্লাস',
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(16,185,129,0.1),transparent_50%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto">
            <div>
              <span className="inline-block px-4 py-1.5 mb-6 text-sm font-semibold tracking-wider text-emerald-400 uppercase bg-emerald-500/10 rounded-full">
                স্মার্ট লার্নিং প্ল্যাটফর্ম
              </span>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-6 leading-tight">
                {settings.heroTitle.split('EduCoreBD')[0]}
                <span className="text-emerald-400">EduCoreBD</span>
                {settings.heroTitle.split('EduCoreBD')[1]}
              </h1>
              <p className="text-xl text-zinc-400 mb-10 leading-relaxed">
                {settings.heroSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/courses"
                  className="w-full sm:w-auto px-8 py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2"
                >
                  {settings.heroButton1Text} <ArrowRight size={20} />
                </Link>
                <Link
                  to="/free-classes"
                  className="w-full sm:w-auto px-8 py-4 bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl font-bold text-lg border border-zinc-800 transition-all flex items-center justify-center gap-2"
                >
                  <Play size={20} fill="currentColor" /> {settings.heroButton2Text}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 border-y border-zinc-900 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 overflow-hidden">
            {/* Stats Section */}
            {[
              { label: 'সক্রিয় শিক্ষার্থী', value: settings.statsStudents, suffix: '+', icon: Users },
              { label: 'সেরা কোর্সসমূহ', value: settings.statsCourses, suffix: '+', icon: Zap },
              { 
                label: settings.statsTeachers === 1 ? 'Expert Instructor' : 'অভিজ্ঞ শিক্ষক', 
                value: settings.statsTeachers, 
                suffix: settings.statsTeachers > 1 ? '+' : '', 
                icon: Award 
              },
              { label: 'সফলতা হার', value: settings.statsSuccessRate, suffix: '%', icon: BarChart3 },
            ].map((stat, i) => (
              <div 
                key={i} 
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-zinc-900 mb-4 text-emerald-400">
                  <stat.icon size={24} />
                </div>
                <div className="text-3xl font-bold mb-1 col-span-1">
                  <Counter value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-zinc-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl font-bold mb-4">ক্লাস ক্যাটাগরি</h2>
              <p className="text-zinc-400">আপনার ক্লাসের জন্য সেরা কোর্সটি বেছে নিন</p>
            </div>
          </div>
          <div className={`grid grid-cols-1 ${homepageSettings?.showSkillCourses ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-6`}>
            {[
              { 
                id: 'school', 
                name: 'স্কুল', 
                desc: 'Class 6 - 8 এর সকল বিষয়',
                image: settings.schoolImage 
              },
              { 
                id: 'academic', 
                name: 'একাডেমিক', 
                desc: 'Class 9, 10, SSC & HSC',
                image: settings.academicImage
              },
              { 
                id: 'crc', 
                name: 'CRC', 
                desc: 'Compact Revision Course',
                image: settings.crcImage
              },
              ...(homepageSettings?.showSkillCourses ? [{
                id: 'skill',
                name: 'Upgrade Courses',
                desc: 'উন্নত দক্ষতা অর্জনের কোর্সসমূহ',
                image: settings.skillImage
              }] : [])
            ].map((cat) => (
              <Link
                key={cat.id}
                to={`/courses?category=${cat.id}`}
                className="group relative h-64 rounded-3xl overflow-hidden border border-zinc-800 hover:border-emerald-500/50 transition-all"
              >
                <img
                  src={optimizeImageUrl(cat.image, 's400') || undefined}
                  alt={cat.name}
                  width={400}
                  height={300}
                  loading="lazy"
                  className="object-cover w-full h-full opacity-70 group-hover:scale-110 transition-transform duration-700"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent" />
                <div className="absolute bottom-8 left-8">
                  <h3 className="text-4xl font-bold mb-2 tracking-tighter">{cat.name}</h3>
                  <p className="text-zinc-400">{cat.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="py-20 bg-zinc-900/30 overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="flex justify-between items-end">
            <div>
              <h2 className="text-3xl font-bold mb-4 text-white">জনপ্রিয় কোর্সসমূহ</h2>
              <p className="text-zinc-400">শিক্ষার্থীদের পছন্দের তালিকায় থাকা সেরা কোর্সসমূহ</p>
            </div>
            <Link to="/courses" className="text-blue-500 font-semibold hover:underline flex items-center gap-1">
              সবগুলো দেখুন <ArrowRight size={18} />
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <div className="w-10 h-10 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="relative flex overflow-hidden">
            <div 
              className="animate-marquee flex gap-6 px-6"
              style={{ '--duration': `${Math.max(20, visibleCourses.length * 8)}s` } as React.CSSProperties}
            >
              {/* Render courses multiple times for seamless infinite loop */}
              {visibleCourses.length > 0 ? (
                [...visibleCourses, ...visibleCourses, ...visibleCourses].map((course, idx) => (
                  <div key={`${course.id}-${idx}`} className="w-[280px] sm:w-[320px] flex-shrink-0">
                    <CourseCard course={course} />
                  </div>
                ))
              ) : (
                <div className="text-zinc-500 py-12 text-center w-full">কোন জনপ্রিয় কোর্স এই মুহূর্তে নেই।</div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Free Class CTA */}
      <section className="py-20 cta-selection-yellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-600 rounded-[3rem] p-12 md:p-20 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
            <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                  {settings.freeClassTitle}
                </h2>
                <p className="text-red-100 text-lg mb-10 leading-relaxed">
                  {settings.freeClassSubtitle}
                </p>
                <Link
                  to="/free-classes"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-red-600 rounded-xl font-bold text-lg hover:bg-red-50 transition-all shadow-xl"
                >
                  ফ্রি ক্লাস দেখুন <Play size={20} fill="currentColor" />
                </Link>
              </div>
              <div className="hidden md:block">
                <img 
                  src={optimizeImageUrl(settings.freeClassImage, 's600') || undefined} 
                  alt="Free Class" 
                  width={600}
                  height={450}
                  loading="lazy"
                  className="rounded-3xl shadow-2xl hover:scale-105 transition-transform duration-500 w-full object-cover max-h-[500px]"
                  referrerPolicy="no-referrer"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
